import { liteClient } from 'algoliasearch/lite'

const GROUPS = ['products', 'content', 'articles', 'recipes'] as const
type Group = (typeof GROUPS)[number]
const ALIASES = { content: 'subpage', articles: 'newsArticle', recipes: 'recipesArticle' } as const
const PRE = '__noi-highlight__'
const POST = '__/noi-highlight__'
const DEBOUNCE_MS = 500
const LABELS = {
  minimum: 'Sláðu inn að minnsta kosti {minimum} stafi.',
  loading: 'Leita…',
  error: 'Ekki tókst að sækja niðurstöður. Reyndu aftur.',
  results: '{count} niðurstöður fyrir „{query}“.',
  empty: 'Engar niðurstöður fundust.',
  page: 'Síða',
  news: 'Frétt',
  recipe: 'Uppskrift',
  previous: 'Fyrri síða',
  next: 'Næsta síða',
}
type Config = {
  applicationId: string
  searchApiKey: string
  productIndexName: string
  contentIndexName: string
  minimumQueryLength?: number
  hitsPerPage?: number
  labels?: Partial<typeof LABELS>
}
type Hit = {
  objectID: string
  title?: string
  name?: string
  pageTitle?: string
  nodeName?: string
  contentTypeAlias?: string
  url?: string
  sku?: string
  image_url?: string
  _highlightResult?: Record<string, { value?: string }>
}
type Response = { hits: Hit[]; nbHits: number; nbPages: number; facets?: Record<string, Record<string, number>> }

/** Never accept executable schemes, protocol-relative URLs or control-character obfuscation. */
export const safeSearchUrl = (value: unknown, base: string): string | null => {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: Reject URL scheme obfuscation before parsing.
  if (typeof value !== 'string' || !value.trim() || /[\u0000-\u0020\u007f\\]/.test(value) || value.startsWith('//')) {
    return null
  }
  try {
    const url = new URL(value, base)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}

/** Index text is untrusted: create text/mark nodes, never parse the highlight as HTML. */
export const appendSearchHighlight = (element: HTMLElement, value: string) => {
  const parts = value.split(PRE)
  element.append(document.createTextNode(parts[0]))
  for (const part of parts.slice(1)) {
    const end = part.indexOf(POST)
    if (end === -1) {
      element.append(document.createTextNode(PRE + part))
      continue
    }
    const mark = document.createElement('mark')
    mark.textContent = part.slice(0, end)
    element.append(mark, document.createTextNode(part.slice(end + POST.length)))
  }
}

/** Search-only browser client; the optional client is a mock test seam. */
export const mountInstantSearch = (root: HTMLElement, injectedClient?: ReturnType<typeof liteClient>) => {
  const configElement = root.querySelector<HTMLScriptElement>('script[data-search-config]')
  const form = root.querySelector<HTMLFormElement>('form')
  const input = root.querySelector<HTMLInputElement>('input[name="q"]')
  const status = root.querySelector<HTMLElement>('[data-search-status]')
  const results = root.querySelector<HTMLElement>('[data-search-results]')
  const loader = root.querySelector<HTMLElement>('[data-search-loader]')
  const tabs = root.querySelector<HTMLElement>('[data-search-tabs]')
  if (!configElement || !form || !input || !status || !results || !tabs || root.dataset.searchDisabled === 'true') {
    return () => {}
  }
  let config: Config
  try {
    config = JSON.parse(configElement.textContent || '{}') as Config
  } catch {
    return () => {}
  }
  if (
    !config ||
    [config.applicationId, config.searchApiKey, config.productIndexName, config.contentIndexName].some(
      (value) => typeof value !== 'string' || !value.trim()
    )
  ) {
    return () => {}
  }
  const elements = GROUPS.map((group) => ({
    group,
    tab: root.querySelector<HTMLElement>(`[data-search-tab="${group}"]`),
    panel: root.querySelector<HTMLElement>(`[data-search-panel="${group}"]`),
    list: root.querySelector<HTMLElement>(`[data-search-hits="${group}"]`),
    pagination: root.querySelector<HTMLElement>(`[data-search-pagination="${group}"]`),
  }))
  if (elements.some(({ tab, panel, list, pagination }) => !tab || !panel || !list || !pagination)) {
    return () => {}
  }
  const minimum = Math.max(2, Math.min(20, Math.floor(config.minimumQueryLength || 2)))
  const pageSize = Math.max(1, Math.min(20, Math.floor(config.hitsPerPage || 8)))
  const labels = { ...LABELS, ...config.labels }
  const client = injectedClient || liteClient(config.applicationId, config.searchApiKey)
  const totals: Record<Group, number> = { products: 0, content: 0, articles: 0, recipes: 0 }
  const pages = { ...totals }
  const pageCounts = { ...totals }
  let selected: Group = 'products'
  let term = ''
  let discovered = false
  let autoSelect = true
  let version = 0
  let disposed = false
  let loading = false
  let failed = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let rows: HTMLAnchorElement[] = []
  let active = -1

  const resetActive = () => {
    active = -1
    input.removeAttribute('aria-activedescendant')
    rows.forEach((row) => {
      row.classList.remove('search-results__card--active')
      row.setAttribute('aria-selected', 'false')
    })
  }
  const sync = () => {
    tabs.hidden = term.length < minimum || failed
    for (const { group, tab, panel, list } of elements) {
      if (!tab || !panel || !list) {
        continue
      }
      tab.id ||= `search-tab-${group}`
      panel.id ||= `search-panel-${group}`
      list.id ||= `${panel.id}-list`
      tab.setAttribute('role', 'tab')
      tab.setAttribute('aria-controls', panel.id)
      tab.setAttribute('aria-selected', String(selected === group))
      tab.tabIndex = selected === group ? 0 : -1
      tab.classList.toggle('tab__header--selected', selected === group)
      panel.setAttribute('role', 'tabpanel')
      panel.setAttribute('aria-labelledby', tab.id)
      panel.classList.toggle('tab__content--selected', selected === group)
      panel.hidden = selected !== group || term.length < minimum || failed
      list.classList.add(group === 'products' ? 'cardsflex__container' : 'search-results__list')
      list.setAttribute('role', 'listbox')
      list.setAttribute('aria-labelledby', tab.id)
      root.querySelector<HTMLElement>(`[data-search-count="${group}"]`)?.replaceChildren(String(totals[group]))
    }
    input.setAttribute('aria-controls', elements.find(({ group }) => group === selected)?.list?.id || '')
    results.setAttribute('aria-busy', String(loading))
    if (loader) {
      loader.hidden = !loading
    }
  }
  const announce = (template: string) => {
    status.textContent = template
      .replace('{minimum}', String(minimum))
      .replace('{count}', String(GROUPS.reduce((sum, group) => sum + totals[group], 0)))
      .replace('{query}', term)
    sync()
  }
  const clear = () => {
    resetActive()
    rows = []
    for (const { list, pagination, group } of elements) {
      list?.replaceChildren()
      pagination?.replaceChildren()
      if (pagination) {
        pagination.hidden = true
      }
      const empty = root.querySelector<HTMLElement>(`[data-search-empty="${group}"]`)
      if (empty) {
        empty.hidden = true
      }
    }
    input.setAttribute('aria-expanded', 'false')
  }
  const paint = (hits: Hit[], focusPage: boolean) => {
    clear()
    const target = elements.find(({ group }) => group === selected)
    if (!target?.list || !target.pagination) {
      return
    }
    for (const hit of hits) {
      const href = safeSearchUrl(hit.url, window.location.href)
      if (!href) {
        continue
      }
      const link = document.createElement('a')
      link.id = `${input.id || 'noi-search'}-result-${rows.length}`
      link.className =
        selected === 'products'
          ? 'card card__link card--image-overflow card-w-controls search-results__card'
          : 'search-results__card'
      link.href = href
      link.setAttribute('role', 'option')
      link.setAttribute('aria-selected', 'false')
      if (selected === 'products') {
        const imageContainer = document.createElement('div')
        imageContainer.className = 'card__image'
        const src = safeSearchUrl(hit.image_url, window.location.href)
        if (src) {
          const thumbnail = (width: number) => {
            const url = new URL(src)
            url.searchParams.set('width', String(width))
            url.searchParams.set('height', width === 400 ? '480' : '240')
            url.searchParams.set('rmode', 'boxpad')
            return url.href
          }
          const picture = document.createElement('picture')
          const source = document.createElement('source')
          source.media = '(max-width: 768px)'
          source.srcset = `${thumbnail(400)} 2x, ${thumbnail(200)} 1x`
          const image = document.createElement('img')
          image.src = thumbnail(200)
          image.srcset = source.srcset
          image.alt = hit.title || hit.nodeName || ''
          image.loading = 'lazy'
          picture.append(source, image)
          imageContainer.append(picture)
        }
        link.append(imageContainer)
      }
      const label = document.createElement(selected === 'products' ? 'h3' : 'span')
      label.className = selected === 'products' ? 'card__title' : 'search-results__name'
      const attribute = selected === 'products' ? 'title' : hit.pageTitle ? 'pageTitle' : 'name'
      appendSearchHighlight(label, hit._highlightResult?.[attribute]?.value || hit[attribute] || hit.nodeName || '')
      link.append(label)
      if (selected === 'products') {
        const sku = document.createElement('p')
        sku.className = 'card__text'
        sku.textContent = hit.sku || ''
        link.append(sku)
      } else {
        const badge = document.createElement('span')
        badge.className = 'search-results__type'
        badge.textContent = selected === 'articles' ? labels.news : selected === 'recipes' ? labels.recipe : labels.page
        link.append(badge)
      }
      if (selected === 'products') {
        const wrapper = document.createElement('div')
        wrapper.className = 'cardsflex__item card__parent card__wrapper'
        wrapper.setAttribute('role', 'presentation')
        const background = document.createElement('div')
        background.className = 'card__bg'
        background.setAttribute('aria-hidden', 'true')
        wrapper.append(link, background)
        target.list.append(wrapper)
      } else {
        target.list.append(link)
      }
      rows.push(link)
    }
    input.setAttribute('aria-expanded', String(rows.length > 0))
    const empty = root.querySelector<HTMLElement>(`[data-search-empty="${selected}"]`)
    if (empty) {
      empty.hidden = hits.length > 0
    }
    const count = pageCounts[selected]
    const current = pages[selected]
    target.pagination.classList.add('search-results__pagination')
    target.pagination.setAttribute('aria-label', labels.page)
    const addPage = (page: number, text: string, disabled = false) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.dataset.searchPage = String(page)
      button.textContent = text
      button.disabled = disabled
      if (page === current && /^\d+$/.test(text)) {
        button.setAttribute('aria-current', 'page')
      }
      button.setAttribute('aria-label', /^\d+$/.test(text) ? `${labels.page} ${text}` : text)
      target.pagination?.append(button)
    }
    if (count > 1) {
      target.pagination.hidden = false
      addPage(current - 1, labels.previous, current === 0)
      // Five consecutive pages plus the first/last, bounded by actual Algolia nbPages.
      const start = Math.max(0, Math.min(current - 2, count - 5))
      const end = Math.min(count, start + 5)
      const windowPages = new Set([0, ...Array.from({ length: end - start }, (_, i) => start + i), count - 1])
      let previous = -1
      for (const page of windowPages) {
        if (page - previous > 1) {
          target.pagination.append(document.createTextNode(' … '))
        }
        addPage(page, String(page + 1))
        previous = page
      }
      addPage(current + 1, labels.next, current >= count - 1)
      if (focusPage) {
        target.pagination.querySelector<HTMLButtonElement>('[aria-current="page"]')?.focus()
      }
    }
    announce(GROUPS.some((group) => totals[group] > 0) ? labels.results : labels.empty)
  }
  const refresh = async (focusPage = false) => {
    clearTimeout(timer)
    const requestVersion = ++version
    if (term.length < minimum) {
      loading = false
      announce(labels.minimum)
      return
    }
    loading = true
    failed = false
    clear()
    announce(labels.loading)
    const isCurrent = () => !disposed && version === requestVersion
    const batch = async (filtered: boolean) => {
      const contentGroup = selected === 'products' ? 'content' : selected
      const response = await client.search<Hit>({
        requests: [
          {
            indexName: config.productIndexName,
            query: term,
            page: pages.products,
            hitsPerPage: pageSize,
            attributesToRetrieve: ['objectID', 'title', 'nodeName', 'url', 'sku', 'image_url'],
            attributesToSnippet: [],
            attributesToHighlight: ['title'],
            highlightPreTag: PRE,
            highlightPostTag: POST,
          },
          {
            indexName: config.contentIndexName,
            query: term,
            page: filtered ? pages[contentGroup] : 0,
            hitsPerPage: pageSize,
            facets: ['contentTypeAlias'],
            facetingAfterDistinct: true,
            ...(filtered ? { facetFilters: [`contentTypeAlias:${ALIASES[contentGroup]}`] } : {}),
            attributesToRetrieve: ['objectID', 'name', 'pageTitle', 'url', 'contentTypeAlias'],
            attributesToSnippet: [],
            attributesToHighlight: ['name', 'pageTitle'],
            highlightPreTag: PRE,
            highlightPostTag: POST,
          },
        ],
      })
      return response.results as Response[]
    }
    try {
      const filtered = discovered && selected !== 'products'
      let responses = await batch(filtered)
      if (!isCurrent()) {
        return
      }
      totals.products = responses[0].nbHits || 0
      pageCounts.products = responses[0].nbPages || 0
      if (!discovered) {
        // Missing content facets must not discard valid product results.
        // Category totals require Algolia's contentTypeAlias facet, not sampled hits.
        const facets = responses[1].facets?.contentTypeAlias || {}
        for (const group of ['content', 'articles', 'recipes'] as const) {
          totals[group] = facets[ALIASES[group]] || 0
        }
        discovered = true
        if (autoSelect) {
          selected = GROUPS.find((group) => totals[group] > 0) || 'products'
        }
        sync()
      }
      if (selected !== 'products' && !filtered) {
        const contentResponse = responses[1]
        const alias = ALIASES[selected]
        const allTarget = contentResponse.hits.every((hit) => hit.contentTypeAlias === alias)
        // Discovery can be reused only if it is the complete category's first page.
        if (!allTarget || contentResponse.nbHits !== totals[selected] || pages[selected] !== 0) {
          responses = await batch(true)
          if (!isCurrent()) {
            return
          }
        }
      }
      const response = selected === 'products' ? responses[0] : responses[1]
      pageCounts[selected] = response.nbPages || 0
      loading = false
      paint(response.hits, focusPage)
    } catch {
      if (!isCurrent()) {
        return
      }
      loading = false
      failed = true
      clear()
      announce(labels.error)
    }
  }
  const change = (immediate = false) => {
    const next = input.value.trim()
    if (!immediate && next === term) {
      return
    }
    clearTimeout(timer)
    version++
    if (next !== term) {
      term = next
      discovered = false
      autoSelect = true
      selected = 'products'
      for (const group of GROUPS) {
        pages[group] = pageCounts[group] = totals[group] = 0
      }
    }
    resetActive()
    loading = false
    failed = false
    results.setAttribute('aria-busy', 'false')
    if (loader) {
      loader.hidden = true
    }
    if (term.length < minimum) {
      clear()
      announce(labels.minimum)
    }
    if (immediate) {
      void refresh()
    } else if (term.length >= minimum) {
      timer = setTimeout(() => void refresh(), DEBOUNCE_MS)
    }
  }
  const selectTab = (group: Group) => {
    autoSelect = false
    selected = group
    resetActive()
    sync()
    void refresh()
  }
  const onInput = (event: Event) => {
    if (!(event as InputEvent).isComposing) {
      change()
    }
  }
  const onCompositionEnd = () => change()
  const onSubmit = (event: Event) => {
    event.preventDefault()
    const url = new URL(window.location.href)
    if (input.value.trim()) {
      url.searchParams.set('q', input.value.trim())
    } else {
      url.searchParams.delete('q')
    }
    window.history.pushState(null, '', url)
    change(true)
  }
  const onClick = (event: MouseEvent) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-search-tab], [data-search-page]')
    if (!target || !root.contains(target)) {
      return
    }
    event.preventDefault()
    const group = target.dataset.searchTab as Group
    if (GROUPS.includes(group)) {
      selectTab(group)
    } else if (target.dataset.searchPage && !(target as HTMLButtonElement).disabled) {
      const page = Number(target.dataset.searchPage)
      if (Number.isInteger(page) && page >= 0 && page < pageCounts[selected] && page !== pages[selected]) {
        autoSelect = false
        pages[selected] = page
        void refresh(true)
      }
    }
  }
  const onKeydown = (event: KeyboardEvent) => {
    if (event.isComposing) {
      return
    }
    const tab = (event.target as HTMLElement).closest<HTMLElement>('[data-search-tab]')
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault()
      const index = GROUPS.indexOf(tab.dataset.searchTab as Group)
      const next =
        event.key === 'Home' ? 0 : event.key === 'End' ? 3 : (index + (event.key === 'ArrowRight' ? 1 : 3)) % 4
      elements[next].tab?.focus()
      selectTab(GROUPS[next])
      return
    }
    if (event.target !== input && !rows.includes(event.target as HTMLAnchorElement)) {
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      resetActive()
    }
    if (['ArrowDown', 'ArrowUp'].includes(event.key) && rows.length) {
      event.preventDefault()
      const focused = rows.indexOf(document.activeElement as HTMLAnchorElement)
      if (focused >= 0) {
        active = focused
      }
      active = (active + (event.key === 'ArrowDown' ? 1 : active < 0 ? 0 : -1) + rows.length) % rows.length
      rows.forEach((row, index) => {
        row.classList.toggle('search-results__card--active', active === index)
        row.setAttribute('aria-selected', String(active === index))
      })
      input.setAttribute('aria-activedescendant', rows[active].id)
      rows[active].scrollIntoView?.({ block: 'nearest' })
      if (event.target !== input) {
        rows[active].focus()
      }
    }
    if (event.key === 'Enter' && event.target === input && active >= 0) {
      event.preventDefault()
      rows[active].click()
    }
  }
  const onPopstate = () => {
    input.value = new URL(window.location.href).searchParams.get('q') || ''
    change(true)
  }
  status.setAttribute('role', 'status')
  status.setAttribute('aria-live', 'polite')
  input.setAttribute('role', 'combobox')
  input.setAttribute('aria-autocomplete', 'list')
  tabs.setAttribute('role', 'tablist')
  tabs.setAttribute('aria-orientation', 'horizontal')
  form.addEventListener('submit', onSubmit)
  input.addEventListener('input', onInput)
  input.addEventListener('compositionend', onCompositionEnd)
  root.addEventListener('keydown', onKeydown)
  root.addEventListener('click', onClick)
  window.addEventListener('popstate', onPopstate)
  input.value = new URL(window.location.href).searchParams.get('q') ?? input.value
  change(true)
  return () => {
    disposed = true
    version++
    clearTimeout(timer)
    form.removeEventListener('submit', onSubmit)
    if (loader) {
      loader.hidden = true
    }
    results.setAttribute('aria-busy', 'false')
    input.removeEventListener('input', onInput)
    input.removeEventListener('compositionend', onCompositionEnd)
    root.removeEventListener('keydown', onKeydown)
    root.removeEventListener('click', onClick)
    window.removeEventListener('popstate', onPopstate)
  }
}

export default { init: mountInstantSearch }
